import React, { useEffect, useState } from 'react';
import { enablePushNotifications, isPushSupported, isIOS, isStandalone } from './pushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SNOOZE_KEY = 'pushReenableSnoozeUntil';

const authHeaders = (json = false) => {
  const token = localStorage.getItem('session_token');
  return { ...(json ? { 'Content-Type': 'application/json' } : {}), ...(token ? { 'X-Session-Token': token } : {}) };
};

const saveTimezone = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!timezone) return;
  fetch(`${API_URL}/api/users/me/timezone`, {
    method: 'POST', credentials: 'include', headers: authHeaders(true), body: JSON.stringify({ timezone })
  }).catch(() => {});
};

const isReceivingOnThisDevice = async () => {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription().catch(() => null) : null;
  if (!sub) return false;
  const res = await fetch(`${API_URL}/api/push/devices`, { credentials: 'include', headers: authHeaders() });
  if (!res.ok) return true; // can't tell - don't nag
  const { devices = [] } = await res.json();
  return devices.some((d) => d.endpoint_tail === sub.endpoint.slice(-12));
};

export const NotificationReenablePrompt = ({ isDarkMode = false }) => {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    saveTimezone();
    const snoozedUntil = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (Date.now() < snoozedUntil || !isPushSupported() || (isIOS() && !isStandalone())) return;
    if (Notification.permission === 'denied') return;
    isReceivingOnThisDevice().then((ok) => setShow(!ok)).catch(() => {});
  }, []);

  const turnOn = async () => {
    setBusy(true);
    setMessage('');
    try {
      await enablePushNotifications();
      setIsError(false);
      setMessage("You're all set! Notifications are on for this phone.");
      setTimeout(() => setShow(false), 2000);
    } catch (e) {
      setIsError(true);
      setMessage(e.message || 'Could not turn on notifications');
    } finally {
      setBusy(false);
    }
  };

  const notNow = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + 3 * 24 * 60 * 60 * 1000));
    setShow(false);
  };

  if (!show) return null;
  return (
    <div data-testid="push-reenable-prompt" className="fixed inset-x-0 top-0 z-[60] p-3" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
      <div className={`max-w-lg mx-auto rounded-2xl shadow-2xl border p-4 ${isDarkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
        <p className="font-semibold">Turn notifications back on</p>
        <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          This phone isn't getting alerts for messages, workout reminders and rest timers. One tap fixes it.
        </p>
        {message && <p data-testid="push-reenable-message" className={`text-sm mt-2 whitespace-pre-line ${isError ? 'text-red-500' : 'text-blue-600'}`}>{message}</p>}
        <div className="flex gap-2 mt-3">
          <button data-testid="push-reenable-turn-on-btn" onClick={turnOn} disabled={busy}
            className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white font-semibold disabled:opacity-50">
            {busy ? 'Turning on...' : 'Turn on'}
          </button>
          <button data-testid="push-reenable-not-now-btn" onClick={notNow}
            className={`px-4 py-2.5 rounded-xl font-medium ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};
